import { useContext, useState } from "react";
import { AccountTreatmentWithState } from "../AccountState";
import { useRemoveAccountTreatment } from "../../../../sdk/ams/account/UseRemoveAccountTreatment";
import { useRefreshAccountId } from "../../../../sdk/ams";
import { AccountContext } from "../../AccountContext";
import { useFormatAndSendTreatmentRequest } from "../hooks/useAddTreatment";
import { toFormattedDate } from "../../../../utilities/FormatHelpers";
import {  Select } from '@app/shared';
import DatePicker from 'react-datepicker';
import "./TreatmentList.css";
import { IconGroup } from "semantic-ui-react";
import { expiryPeriodOptions } from "../../constants/expiry-period-options";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { useUpdateAccountTreatment } from "../../../../sdk/ams/account/UseUpdateAccountTreatment";
import { Loading, LoadingWrapper } from "../../../tms/components/LoadingWrapper";

const CAN_EDIT = true;
export default function TreatmentList(props: {title: string, treatments: AccountTreatmentWithState[], color?: string, activeTreatments: boolean}) {
    const [editing, setEditing] = useState<string>();
    const [minExpiryDate, setMinExpiryDate] = useState<Date | undefined>(new Date());

    const [showMore, setShowMore] = useState<boolean>(false);

    const { accountId, billingDate } = useContext(AccountContext);
    const refreshAccount = useRefreshAccountId(accountId);

    const {
        mutateAsync: removeAccountTreatment,
    } = useRemoveAccountTreatment();



    const onRemove = async (id) => { 
        await removeAccountTreatment({accountId, treatmentId: id});
        refreshAccount();
    }

    const EffectiveDate = (props: {treatment: AccountTreatmentWithState,  editingRow: string}) => {
      const treatment = props.treatment;
      if(props.editingRow != treatment.id) return <td>{toFormattedDate(treatment.effectiveDate) ?? 'Effective date not set'}</td>
      return <td width={'20%'} >
        <div className="ui form static" style={{position: 'static'}}>
        <Controller
          name="effectiveDate"
          control={formContext.control}
          render={({ field }) => (
            <DatePicker
                name="effectiveDate"
                selected={field.value}
                onChange={(date) => {
                  if(date) {
                    setMinExpiryDate(date)
                  }
                  field.onChange(date);
                }}
                minDate={billingDate}
                dateFormat="dd/MM/yyyy"
                placeholderText="Effective date"
                isClearable={true}
            />
            )}
          />
        </div>
        </td>
    }

    const ExpiryDate = (props: {treatment: AccountTreatmentWithState, editingRow: string}) => {
      const treatment = props.treatment;
      if(props.editingRow != treatment.id) return <td>{treatment.expiryDate?.length ? toFormattedDate(treatment.expiryDate) :  'Expiry date not set'}</td>
      return <td width={'20%'} >
        <div className="ui form static" style={{position: 'static'}}>
        <Controller
          name="expiryDate"
          control={formContext.control}
          render={({ field }) => (
            <DatePicker
                name="expiryDate"
                selected={field.value}
                onChange={(date) => {
                  if(date) {
                    formContext.setValue('numberOfBillingPeriods', null);
                  }
                  field.onChange(date);
                }}
                minDate={minExpiryDate}
                dateFormat="dd/MM/yyyy"
                placeholderText="Expiry date"
                isClearable
            />
            )}
          />
        </div>
      </td>
    }

    const formContext = useForm<{
      treatmentId: string;
      effectiveDate?: string;
      expiryDate?: string;
      numberOfBillingPeriods?: number | string;
    }>();
    const { mutateAsync: updateAccountTreatment, isPending } = useUpdateAccountTreatment();

    const onUpdateTreatment = useFormatAndSendTreatmentRequest(accountId, updateAccountTreatment, refreshAccount);
    
    return ( 
        <LoadingWrapper loading={isPending}>
            <div className="treatment-list-title"><h2 className="ui header">{props.title}</h2>
            {props.treatments?.length > 0 && <IconGroup 
              aria-label="Show more"
              tooltip="Show more"
              className="pointer show-more"
              onClick={
                () => setShowMore(!showMore)
              }>
                {showMore ? 'Hide ID' : 'Show ID'}
              </IconGroup>}
            </div>
            {!props.treatments?.length && <p className="no-treatments">No {props.activeTreatments ? 'active' : 'pending'} treatments</p>}

            {props.treatments?.length > 0 && (
              <div className="scrollable" style={{maxHeight: 1000}}>
                <table className={"ui single line table " + props.color}>
                  <thead className="">
                    <tr>
                      {showMore && <th>Id</th>}
                      <th style={{ width: '25%' }}>Name</th>
                      <th>Effective date</th>
                      <th>Expiry date</th>
                      {editing && <th>Expiry period</th>}
                      <th style={{ width: '10%' }}>Actions</th>

                    </tr>
                  </thead>

                  <tbody>
                    {props.treatments.map((treatment) => (
                      <tr key={treatment.id}>
                        {showMore && <td>{treatment.id}</td>}
                        {editing != treatment.id && 
                        <>
                          <td className="large"><h5>{treatment.name}</h5></td>
                          <EffectiveDate treatment={treatment} editingRow={null} />
                          <ExpiryDate treatment={treatment} editingRow={null} />
                          {editing?.length > 0 && <td></td>}
                        </>
                        }
                        { editing == treatment.id &&
                        <>
                        <FormProvider {...formContext}>
                            <td className="large"><h5>{treatment.name}</h5></td>
                            <EffectiveDate treatment={treatment} editingRow={!props.activeTreatments ? editing : null} />
                            <ExpiryDate treatment={treatment} editingRow={editing} />
                            <td>
                            <Select
                              className="ui fluid dropdown"
                                name="numberOfBillingPeriods"
                                hideLabel={true}
                                onChange={(ev) => {
                                  if (ev.target.value.length > 0) {
                                    formContext.setValue('expiryDate', null);
                                  }
                                }}
                                selectOptions={expiryPeriodOptions}
                                label="Expiry Period"
                              />
                            </td>
              
                        </FormProvider>
                        </>}
             
                        <td>
                            {(editing != treatment.id && CAN_EDIT) && <button className="ui button blue" 
                              onClick={() => {
                                formContext.setValue("treatmentId", treatment.id);
                                if(!props.activeTreatments && treatment.effectiveDate) formContext.setValue("effectiveDate", new Date(treatment.effectiveDate) as any);
                                if(treatment.expiryDate) {
                                  formContext.setValue("expiryDate", new Date(treatment.expiryDate) as any);
                                }
                                setEditing(treatment.id)
                                }}>Edit</button>}
                             {editing && editing == treatment.id && <button className="ui button red" 
                            onClick={async () => {
                              setEditing(null)
                              formContext.reset();
                              }}>Cancel</button>}
                            {editing && editing == treatment.id && <button className="ui button green"
                              onClick={() => {
                                formContext.handleSubmit(onUpdateTreatment)();
                                setEditing(null);
                              }}
                            >Save</button>}
                            {!editing && <button className="ui button red" onClick={() => onRemove(treatment.id)}>Remove</button>}
                        </td>
                      </tr>
                    ))}
                    </tbody>
                </table>
                </div>
            )}
        </LoadingWrapper>
      
 )   
        
}